import { useMemo } from "react";
import type { CatalogRangeFilter } from "@apex/shared";
import { cn } from "@apex/shared";

export interface SortOption {
  /** The internal sort key (e.g. "name-asc", "range:workingHeight:desc"). */
  value: string;
  /** Human-readable label. */
  label: string;
}

/**
 * Build the canonical sort option set for the Models step. The first three
 * options (Featured / Name asc / Name desc) are always present; per range
 * filter we auto-derive high→low and low→high pairs.
 *
 * Pure function (separately exported) so tests can assert the auto-derivation
 * without spinning up the full dropdown.
 */
export function buildSortOptions(
  rangeFilters: readonly CatalogRangeFilter[]
): readonly SortOption[] {
  const options: SortOption[] = [
    { value: "featured", label: "Featured" },
    { value: "name-asc", label: "Name (A → Z)" },
    { value: "name-desc", label: "Name (Z → A)" },
  ];

  for (const filter of rangeFilters) {
    options.push({
      value: `range:${filter.key}:desc`,
      label: `${filter.displayName} (high → low)`,
    });
    options.push({
      value: `range:${filter.key}:asc`,
      label: `${filter.displayName} (low → high)`,
    });
  }

  return options;
}

export interface SortDropdownProps {
  /** Active sort key. */
  value: string;
  /** Called when the user picks a different option. */
  onChange: (value: string) => void;
  /** Range filters that drive the auto-derived options. */
  rangeFilters: readonly CatalogRangeFilter[];
  className?: string;
}

/**
 * SortDropdown — native `<select>` with auto-derived options. Native control
 * is intentional: free mobile keyboard support + no Shadow-DOM portal hacks
 * needed for the option list. The widget's existing `<Select>` renders a
 * decorated native select; we lift its style here so we can label it
 * with a leading "Sort:" affordance without breaking accessible association.
 */
export function SortDropdown({
  value,
  onChange,
  rangeFilters,
  className,
}: SortDropdownProps) {
  const options = useMemo(() => buildSortOptions(rangeFilters), [rangeFilters]);

  return (
    <label
      className={cn(
        "apex-inline-flex apex-items-center apex-gap-2 apex-text-sm",
        className
      )}
    >
      <span className="apex-text-xs apex-font-medium apex-text-muted-foreground apex-shrink-0">
        Sort:
      </span>
      <select
        aria-label="Sort models"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "apex-flex apex-h-9 apex-rounded-md apex-border apex-border-input apex-bg-background apex-px-3 apex-text-sm",
          "focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring focus-visible:apex-ring-offset-2",
          "apex-cursor-pointer apex-pr-8 apex-appearance-none",
          // Same chevron pattern as the main Select component
          "apex-bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23666%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] apex-bg-[length:1.25rem] apex-bg-[right_0.5rem_center] apex-bg-no-repeat"
        )}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}
